import type { ModelProviderMapping, ProviderMappingCatalog } from "../../api/types";
import { groupActiveMappingPrices, isRouteEligible, routeEligibilitySummary } from "../providers/providerMappingView";

// Keep model configuration readiness identical in the catalog and runtime monitor.
export function summarizeModelRoutes(data: ProviderMappingCatalog | undefined) {
  const result = new Map<number, ModelRouteSummary>();
  if (!data) return result;
  const providerByID = new Map(data.providers.map((item) => [item.id, item]));
  const channelByID = new Map(data.channels.map((item) => [item.id, item]));
  const credentialByChannel = new Map(data.credentials.map((item) => [item.channel_id, item]));
  const pricesByMapping = groupActiveMappingPrices(data.prices);
  const mappingsByModel = new Map<number, ModelProviderMapping[]>();
  data.mappings.forEach((mapping) => mappingsByModel.set(mapping.model_id, [...(mappingsByModel.get(mapping.model_id) || []), mapping]));
  for (const model of data.models) {
    const routes = (mappingsByModel.get(model.id) || []).map((mapping) => {
      const channel = channelByID.get(mapping.channel_id);
      const provider = channel ? providerByID.get(channel.provider_id) : undefined;
      const prices = pricesByMapping.get(mapping.id) || [];
      const credential = channel ? credentialByChannel.get(channel.id) : undefined;
      return { mapping, eligible: isRouteEligible(provider, channel, mapping, prices, credential), eligibility: routeEligibilitySummary(provider, channel, mapping, prices, credential) };
    }).sort(compareModelRoutes);
    const eligible = routes.filter((route) => route.eligible);
    result.set(model.id, { eligible: eligible.length, total: routes.length, blocker: routes.find((route) => !route.eligible)?.eligibility });
  }
  return result;
}

type ModelRoute = {
  mapping: ModelProviderMapping;
  eligible: boolean;
  eligibility: string;
};

export type ModelRouteSummary = {
  eligible: number;
  total: number;
  blocker?: string;
};

export function ModelDispatchState({ modelStatus, summary }: { modelStatus: string; summary?: ModelRouteSummary }) {
  if (modelStatus !== "enabled") return <div className="dispatch-state blocked"><strong>不可调度</strong><small>模型已停用</small></div>;
  if (summary?.eligible) return <div className="dispatch-state ready"><strong>可调度</strong><small>{summary.eligible} 条可用路径</small></div>;
  if (!summary?.total) return <div className="dispatch-state pending"><strong>待接入</strong><small>尚未配置上游</small></div>;
  return <div className="dispatch-state blocked"><strong>不可调度</strong><small>{summary.blocker || "上游配置未完成"}</small></div>;
}

const generationModeOrder = new Map(["text_to_image", "image_to_image", "text_to_video", "image_to_video", "reference_to_video", "video_edit"].map((mode, index) => [mode, index]));

function compareModelRoutes(left: ModelRoute, right: ModelRoute) {
  return (generationModeOrder.get(left.mapping.generation_mode) ?? 99) - (generationModeOrder.get(right.mapping.generation_mode) ?? 99)
    || left.mapping.route_order - right.mapping.route_order
    || left.mapping.id - right.mapping.id;
}
